import {
  Component,
  computed,
  inject,
  Input,
  OnInit,
  signal,
  effect,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';
import { BaseAuthComponent } from '../../../shared/common/BaseAuthComponent.class';
import { CountryService } from '../../../services';
import { BusinessService } from '../../../services/business';
import { Country } from '../../../shared/models';
import { Client } from '../../../shared/interfaces';
import { PhoneInputComponent } from '../../../ui/phone-input/phone-input';
import { ToastService } from '../../../shared/services';

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;
const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];

@Component({
  selector: 'app-people-add',
  imports: [ReactiveFormsModule, PhoneInputComponent],
  templateUrl: './people-add.html',
  styleUrl: './people-add.css',
})
export class PeopleAdd extends BaseAuthComponent implements OnInit {
  @Input() entityId: string | null = null; // id del cliente/profesional
  @Input() endpoint: 'clients' | 'professionals' | null = null;

  countriesService = inject(CountryService);
  businessService = inject(BusinessService);
  private toastService = inject(ToastService);
  private fb = inject(FormBuilder);

  // El store expone un signal con la lista
  countries$ = this.countriesService.getStore().getModel();

  // Guardamos el país a la espera de que lleguen los países
  private pendingCountryName = signal<string | null>(null);
  private pendingCountryId = signal<string | null>(null);

  // Avatar (only for professionals)
  professionalAvatarPreview = signal<string | null>(null);
  private savedAvatarUrl: string | null = null;
  private professionalAvatarFile: File | null = null;
  hasNewAvatar = signal(false);

  /** Email the record had when it was loaded: the API keeps it if the field is emptied */
  savedEmail = signal<string | null>(null);

  isLoadingData = signal<boolean>(false);

  /** Calendar colors to pick from; the custom picker covers anything else */
  readonly colorPresets = [
    '#465FFF', '#12B76A', '#F79009', '#F04438', '#7A5AF8',
    '#EE46BC', '#06AED4', '#FB6514', '#2E90FA', '#667085',
  ];

  peopleForm: FormGroup = this.fb.group({
    firstName: ['', [Validators.required, Validators.maxLength(100)]],
    lastName: ['', [Validators.required, Validators.maxLength(100)]],
    phone: [null, Validators.required], // PhoneInputComponent returns { countryId, phoneNumber } | null
    email: ['', [Validators.email, Validators.maxLength(255)]],
    color: [''],
  });

  private formValue = toSignal(
    this.peopleForm.valueChanges.pipe(startWith(this.peopleForm.value)),
    { initialValue: this.peopleForm.value },
  );

  initials = computed(() => {
    const v = this.formValue();
    const letters = [v.firstName, v.lastName]
      .map((s: string) => (s || '').trim()[0])
      .filter(Boolean)
      .join('')
      .toUpperCase();
    return letters || '?';
  });

  avatarColor = computed(() => this.formValue().color || '#465FFF');

  emailWillBeKept = computed(
    () => this.isEditing() && !!this.savedEmail() && !(this.formValue().email || '').trim(),
  );

  constructor() {
    super();

    // Cuando haya países y tengamos uno pendiente, resolvemos countryId
    effect(() => {
      const countries = this.countries$();
      const pending = this.pendingCountryName();
      const pendingId = this.pendingCountryId();

      if (!countries.length || (!pending && !pendingId)) return;

      const match =
        (pendingId && countries.find((c) => c.countryId === pendingId)) ||
        this.findCountryByName(countries, pending);

      if (match) {
        const currentPhone = this.peopleForm.get('phone')?.value;
        const phoneValue =
          currentPhone && typeof currentPhone === 'object' && currentPhone.phoneNumber
            ? { ...currentPhone, countryId: match.countryId }
            : {
                countryId: match.countryId,
                phoneNumber: currentPhone?.phoneNumber || currentPhone || '',
              };
        this.peopleForm.get('phone')?.setValue(phoneValue, { emitEvent: true });
        this.pendingCountryName.set(null);
        this.pendingCountryId.set(null);
      }
    });
  }

  ngOnInit(): void {
    this.countriesService.fetchAndSetEntitiesToStore();

    if (this.entityId && this.endpoint) {
      this.loadEntity(this.entityId);
    }
  }

  isEditing(): boolean {
    return !!this.entityId;
  }

  isProfessional(): boolean {
    return this.endpoint === 'professionals';
  }

  private loadEntity(entityId: string) {
    this.isLoadingData.set(true);
    this.businessService
      .getRecordByBusinessId<Client>(this.businessId, entityId, this.endpoint!)
      .subscribe({
        next: (record) => {
          this.mapRecordToForm(record);
          this.isLoadingData.set(false);
        },
        error: () => {
          this.isLoadingData.set(false);
          const who = this.isProfessional() ? 'del profesional' : 'del cliente';
          this.toastService.show(`No pudimos cargar los datos ${who}. Cierra y vuelve a intentarlo.`, 'error');
        },
      });
  }

  private mapRecordToForm(record: Client) {
    const r = record as Client & { color?: string | null; avatarUrl?: string | null };
    this.peopleForm.patchValue({
      firstName: r.firstName ?? '',
      lastName: r.lastName ?? '',
      email: r.email ?? '',
      color: r.color ?? '',
    });
    this.savedEmail.set(r.email || null);

    if (this.isProfessional()) {
      this.savedAvatarUrl = r.avatarUrl ?? null;
      this.professionalAvatarPreview.set(this.savedAvatarUrl);
      this.professionalAvatarFile = null;
      this.hasNewAvatar.set(false);
    }

    const phoneValue = { countryId: r.countryId || '', phoneNumber: r.phone || '' };
    const countries = this.countries$();
    const match =
      (r.countryId && countries.find((c) => c.countryId === r.countryId)) ||
      this.findCountryByName(countries, r.countryName);

    if (match) {
      this.peopleForm.get('phone')?.setValue({ ...phoneValue, countryId: match.countryId });
    } else {
      // Resolve once the countries arrive (see the effect in the constructor)
      this.pendingCountryName.set(r.countryName ?? null);
      this.pendingCountryId.set(r.countryId ?? null);
      this.peopleForm.get('phone')?.setValue(phoneValue);
    }
    this.peopleForm.markAsPristine();
  }

  // Normaliza acentos/espacios/mayúsculas para comparar nombres de país
  private normalize(s: string) {
    return (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .trim();
  }

  private findCountryByName(list: Country[], name?: string | null): Country | null {
    if (!name || !list.length) return null;
    const target = this.normalize(name);
    return list.find((c) => this.normalize(c.name) === target) ?? null;
  }

  showError(name: string): boolean {
    const c = this.peopleForm.get(name);
    return !!c && c.invalid && c.touched;
  }

  /** The message shown under a field, in the words a person would use */
  errorFor(name: string): string {
    const c = this.peopleForm.get(name);
    if (!c?.errors) return '';
    if (name === 'firstName') {
      return c.errors['required'] ? 'Escribe el nombre.' : 'El nombre puede tener máximo 100 caracteres.';
    }
    if (name === 'lastName') {
      return c.errors['required'] ? 'Escribe el apellido.' : 'El apellido puede tener máximo 100 caracteres.';
    }
    if (name === 'phone') {
      // The phone input reports null both when empty and when the number doesn't fit the country
      return 'Escribe un celular válido para el país elegido.';
    }
    if (name === 'email') {
      return c.errors['email']
        ? 'Revisa el correo, debe tener la forma nombre@dominio.com.'
        : 'El correo puede tener máximo 255 caracteres.';
    }
    return 'Revisa este dato.';
  }

  setColor(hex: string) {
    this.peopleForm.patchValue({ color: hex });
    this.peopleForm.get('color')?.markAsDirty();
  }

  onCustomColor(event: Event) {
    this.setColor((event.target as HTMLInputElement).value.toUpperCase());
  }

  isValid(): boolean {
    this.peopleForm.markAllAsTouched();
    if (this.peopleForm.valid) return true;

    const invalid = Object.keys(this.peopleForm.controls).filter(
      (k) => this.peopleForm.get(k)?.invalid,
    );
    this.toastService.show(
      invalid.length === 1 ? this.errorFor(invalid[0]) : 'Revisa los campos marcados en rojo.',
      'warning',
      5000,
    );
    const first = document.querySelector<HTMLElement>(`#pa-${invalid[0]}, #pa-${invalid[0]} input`);
    first?.focus();
    return false;
  }

  getValue(): any {
    const { firstName, lastName, email, phone, color } = this.peopleForm.getRawValue();
    const phoneData = phone as { countryId: string; phoneNumber: string } | null;

    return {
      firstName: (firstName || '').trim(),
      lastName: (lastName || '').trim(),
      email: email && email.trim() ? email.trim() : null,
      phone: phoneData?.phoneNumber || '',
      countryId: phoneData?.countryId || '',
      ...(this.isProfessional() && color ? { color } : {}),
      ...(this.isProfessional() ? { avatarFile: this.professionalAvatarFile } : {}),
      entityId: this.entityId,
    };
  }

  onProfessionalAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = ''; // allow picking the same file again
    if (!file) return;

    if (!AVATAR_TYPES.includes(file.type)) {
      this.toastService.show('Elige una imagen JPG, PNG o WebP.', 'warning');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      this.toastService.show('La foto pesa más de 5 MB. Elige una más liviana.', 'warning');
      return;
    }

    this.professionalAvatarFile = file;
    this.hasNewAvatar.set(true);
    const reader = new FileReader();
    reader.onload = () => this.professionalAvatarPreview.set(reader.result as string);
    reader.readAsDataURL(file);
  }

  /** Drops the photo picked in this session and goes back to the saved one */
  discardNewAvatar(): void {
    this.professionalAvatarFile = null;
    this.hasNewAvatar.set(false);
    this.professionalAvatarPreview.set(this.savedAvatarUrl);
  }

  getProfessionalAvatarFile(): File | null {
    return this.professionalAvatarFile;
  }
}
